'use client';

import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '@/context/AuthContext';
import { businessInfoApi } from '@/app/api/business-info';
import { categoriesApi, Category } from '@/app/api/categories';
import TaggedSelect from '@/components/common/TaggedSelect';
import { 
  ArrowPathIcon,
  DocumentTextIcon,
  UserGroupIcon,
  LightBulbIcon,
  ClockIcon,
  SpeakerWaveIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

// Types for article planning
export interface ArticlePlanningData {
  purpose: string;
  targetAudience: string;
  intent: string;
  length?: 'short' | 'medium' | 'long';
  desiredLength?: 'short' | 'medium' | 'long';
  tone: string;
  tags: string[];
  category: string;
  isRegeneration?: boolean;
}

interface ManualModeProps {
  onComplete: (data: ArticlePlanningData) => void;
  initialData?: ArticlePlanningData;
  onStepChange?: (step: ManualModeStep) => void;
  onFormDataChange?: (data: ArticlePlanningData) => void;
  currentStep?: ManualModeStep;
}

export type ManualModeStep = 'purpose' | 'style';

export interface ManualModeRef {
  resetGeneratingState: () => void;
  submit: () => void;
  triggerValidation: () => Promise<boolean>;
}

const ManualMode = forwardRef<ManualModeRef, ManualModeProps>(
  ({ onComplete, initialData, onStepChange, onFormDataChange, currentStep = 'purpose' }, ref) => {
  const { user } = useAuth();
  const totalSteps = 2; // Reduced from 3 to 2 steps
  const [loading, setLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [businessKeywords, setBusinessKeywords] = useState<string[]>([]);
  const [businessCategories, setBusinessCategories] = useState<string[]>([]);
  const [categories, setCategories] = useState<Array<{value: string, label: string}>>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>(initialData?.tags || []);
  const [selectedTone, setSelectedTone] = useState<string>(initialData?.tone || 'professional');
  const [selectedLength, setSelectedLength] = useState<'short' | 'medium' | 'long'>(initialData?.desiredLength || 'medium');
  const [selectedIntents, setSelectedIntents] = useState<string[]>(initialData?.intent ? initialData.intent.split(',') : []);
  const [selectedAudiences, setSelectedAudiences] = useState<string[]>(initialData?.targetAudience ? initialData.targetAudience.split(',') : []);
  const [selectedCategory, setSelectedCategory] = useState<string>(initialData?.category || '');
  const [wordCount, setWordCount] = useState<number>(initialData?.purpose ? initialData.purpose.trim().split(/\s+/).filter(Boolean).length : 0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  
  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    resetGeneratingState: () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setIsGenerating(false);
    },
    submit: () => {
      formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    },
    triggerValidation: async () => {
      if (currentStep === 'purpose') {
        // Validate purpose field
        const purposeValid = await trigger('purpose');
        const purposeText = watch('purpose') || '';
        
        // Check if purpose has at least 20 words
        const wordCount = purposeText.trim().split(/\s+/).filter(Boolean).length;
        if (wordCount < 20) {
          setError('purpose', {
            type: 'manual',
            message: 'Please provide a more detailed description (at least 20 words)'
          });
          return false;
        }
        
        if (!purposeValid) {
          return false;
        }

        // Validate intent
        if (!selectedIntents.length) {
          setError('intent', {
            type: 'manual',
            message: 'Please select at least one purpose for your article.'
          });
          return false;
        }

        // Validate target audience
        if (!selectedAudiences.length) {
          setError('targetAudience', {
            type: 'manual',
            message: 'Please select your target audience.'
          });
          return false;
        }

        return true;
      } else if (currentStep === 'style') {
        // Validate tone
        if (!selectedTone) {
          setError('tone', {
            type: 'manual',
            message: 'Please select a tone of voice'
          });
          return false;
        }
        
        // Validate length
        if (!selectedLength) {
          setError('desiredLength', {
            type: 'manual',
            message: 'Please select a desired article length'
          });
          return false;
        }

        // Validate category
        if (!selectedCategory) {
          setError('category', {
            type: 'manual',
            message: 'Category is required.'
          });
          return false;
        }

        // Keywords are optional - no validation required

        return true;
      }
      
      return false;
    },
  }));

  // Define options for dropdowns
  const intentOptions = [
    { value: 'growth', label: 'Growth - Drive business growth' },
    { value: 'educate', label: 'Educate - Share knowledge' },
    { value: 'inform', label: 'Inform - Provide updates' },
    { value: 'analyze', label: 'Analyze - Examine a topic' },
    { value: 'instruct', label: 'Instruct - Teach a process' },
    { value: 'persuade', label: 'Persuade - Change opinions' },
    { value: 'entertain', label: 'Entertain - Engage readers' },
  ];
  
  const audienceOptions = [
    { value: 'potential', label: 'Potential Customers' },
    { value: 'customers', label: 'Existing Customers' },
    { value: 'beginners', label: 'Beginners' },
    { value: 'intermediate', label: 'Intermediate Knowledge' },
    { value: 'experts', label: 'Industry Experts' },
    { value: 'general', label: 'General Public' },
  ];
  
  const toneOptions = [
    { value: 'professional', label: 'Professional' },
    { value: 'conversational', label: 'Conversational' },
    { value: 'technical', label: 'Technical' },
    { value: 'friendly', label: 'Friendly' },
    { value: 'authoritative', label: 'Authoritative' },
    { value: 'humorous', label: 'Humorous' }
  ];
  
  const lengthOptions = [
    { value: 'short', label: 'Short (300-500 words)' },
    { value: 'medium', label: 'Medium (500-1000 words)' },
    { value: 'long', label: 'Long (1000+ words)' }
  ];

  
  const { register, handleSubmit, setValue, watch, formState: { errors }, trigger, setError } = useForm<ArticlePlanningData>({    resolver: async (data, context, options) => {
      // You can implement custom validation logic here if needed
      return {
        values: data,
        errors: {},
      };
    },
    defaultValues: initialData || {
      purpose: '',
      intent: 'growth',
      targetAudience: 'potential',
      tags: [],
      desiredLength: 'medium',
      tone: 'professional',
      category: '',
    },
    // Register validation rules for all fields
    shouldUnregister: false, // Keep all fields registered
  });

  useEffect(() => {
    register('intent', { required: 'Please select at least one purpose for your article.' });
    register('targetAudience', { required: 'Please select your target audience.' });
    register('category', { required: 'Category is required.' });
    register('tone', { required: 'Tone is required.' });
    register('desiredLength', { required: 'Length is required.' });
    // Keywords are optional, no validation required
  }, [register]);

  // Load business keywords from business info
  // Initialize form state values
  useEffect(() => {
    // Initialize intent and audience from default values
    const intentValue = watch('intent');
    if (typeof intentValue === 'string' && intentValue) {
      setSelectedIntents(intentValue.split(','));
    }
    
    const audienceValue = watch('targetAudience');
    if (typeof audienceValue === 'string' && audienceValue) {
      setSelectedAudiences(audienceValue.split(','));
    }
    
    // Initialize tone and length from default values
    const toneValue = watch('tone');
    if (toneValue) {
      setSelectedTone(toneValue);
    }
    
    const lengthValue = watch('desiredLength');
    if (lengthValue) {
      setSelectedLength(lengthValue);
    }
  }, [watch]);

  useEffect(() => {
    const loadBusinessKeywords = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        // Load categories from API
        const categoriesData = await categoriesApi.getCategories();
        setCategories(categoriesData.map(cat => ({ 
          value: cat.name, 
          label: cat.display_name 
        })));
        
        const businessInfoItems = await businessInfoApi.getAll();
        
        // Find keywords in business info
        const keywordsItem = businessInfoItems.find((item: { key: string }) => item.key === 'keywords');
        const categoriesItem = businessInfoItems.find((item: { key: string }) => item.key === 'categories');

        if (categoriesItem && categoriesItem.value) {
          try {
            const parsedCategories = JSON.parse(categoriesItem.value);
            if (Array.isArray(parsedCategories)) {
              setBusinessCategories(parsedCategories);
            }
          } catch (e) {
            console.error('Error parsing business categories:', e);
          }
        }

        if (keywordsItem && keywordsItem.value) {
          try {
            const parsedKeywords = JSON.parse(keywordsItem.value);
            if (Array.isArray(parsedKeywords)) {
              // Create business keywords with proper value/label pairs
              setBusinessKeywords(parsedKeywords);
              
              // Initialize selected keywords from form data if any
              const formTags = watch('tags');
              if (formTags && Array.isArray(formTags) && formTags.length > 0) {
                setSelectedTags(formTags);
              }
            }
          } catch (e) {
            console.error('Error parsing business keywords:', e);
          }
        }
      } catch (error) {
        console.error('Error loading business info:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadBusinessKeywords();
  }, [user, watch]);

  const handleTagsChange = (value: string | string[]) => {
    const newTags = Array.isArray(value) ? value : [];
    setSelectedTags(newTags);
    setValue('tags', newTags);
  };

  const handleCreateTag = (newTag: string) => {
    if (!selectedTags.includes(newTag)) {
      const newTags = [...selectedTags, newTag];
      setSelectedTags(newTags);
      setValue('tags', newTags);
    }
  };


  // Use a ref to track the previous form data to avoid unnecessary updates
  const prevFormDataRef = useRef<string>('');
  
  // Notify parent of form data changes when relevant state changes

  useEffect(() => {
    if (!onFormDataChange) return;
    
    // Create current form data object
    const currentData: ArticlePlanningData = {
      purpose: watch('purpose') || '',
      targetAudience: selectedAudiences.join(','),
      intent: selectedIntents.join(','),
      desiredLength: selectedLength as 'short' | 'medium' | 'long',
      tone: selectedTone,
      tags: selectedTags || [],
      category: selectedCategory
    };
    
    // Convert to string for comparison to avoid unnecessary updates
    const currentDataString = JSON.stringify(currentData);
    
    // Only notify if data actually changed
    if (currentDataString !== prevFormDataRef.current) {
      prevFormDataRef.current = currentDataString;
      
      // Use a debounced update to prevent too many updates
      const timeoutId = setTimeout(() => {
        onFormDataChange(currentData);
      }, 300);
      
      return () => clearTimeout(timeoutId);
    }
  }, [
    // Only trigger when these values change
    watch('purpose'),
    selectedAudiences.join(','),
    selectedIntents.join(','),
    selectedLength,
    selectedTone,
    selectedTags?.join(','),
    onFormDataChange
  ]);

  const onSubmit = (data: ArticlePlanningData) => {
    // Set loading state
    setIsGenerating(true);
    
    // Get full labels for better LLM understanding
    const getFullLabel = (options: { value: string, label: string }[], value: string) => {
      const option = options.find(opt => opt.value === value);
      return option ? option.label : value;
    };
    
    // Add selected keywords to form data with full labels
    const finalData = {
      ...data,
      // Use the selected values from state
      tags: selectedTags || [],
      tone: getFullLabel(toneOptions, selectedTone),
      desiredLength: getFullLabel(lengthOptions, selectedLength) as any, // Cast to any to avoid type error
      intent: selectedIntents.map(intent => getFullLabel(intentOptions, intent)).join(', '),
      targetAudience: selectedAudiences.map(audience => getFullLabel(audienceOptions, audience)).join(', ')
    };
    
    // Only complete when on the final step and Generate Article button is clicked
    // This prevents automatic transition to the summary page
    onComplete(finalData);
    
    // Set up a timeout to reset the generating state if it's still active after 30 seconds
    // This prevents the button from being stuck in loading state if the API call fails silently
    const timeout = setTimeout(() => {
      setIsGenerating(false);
    }, 30000);
    
    // Store the timeout ID in a ref so we can clear it if needed
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = timeout;
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-6">
      {/* Progress indicator */}
      <div className="mb-8">
      <div className="flex justify-between mt-2">
          <span className={`text-sm ${currentStep === 'purpose' ? 'font-bold text-blue-500' : 'text-gray-500'}`}>Purpose & Audience</span>
          <span className={`text-sm ${currentStep === 'style' ? 'font-bold text-blue-500' : 'text-gray-500'}`}>Style & Tags</span>
        </div>
        <div className="flex items-center justify-between">
          <div className={`flex-1 h-2 ${currentStep === 'purpose' || currentStep === 'style' ? 'bg-blue-500' : 'bg-gray-200'}`}></div>
          <div className={`flex-1 h-2 ${currentStep === 'style' ? 'bg-blue-500' : 'bg-gray-200'}`}></div>
        </div>
      </div>

      <form ref={formRef} onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* Step 1: Purpose & Audience */}
        {currentStep === 'purpose' && (
          <div className="space-y-6">
            <p className="text-gray-600">
              Tell us what you want to write, what is the purpose and who is your audience
            </p>

            <div>
              <div className="flex items-center mb-2">
                <DocumentTextIcon className="h-5 w-5 text-blue-500 mr-2" />
                <label htmlFor="purpose" className="block text-sm font-medium text-gray-700">
                  What do you want to write about?
                </label>
              </div>
              <div className="flex flex-col w-full cursor-text rounded-md bg-white py-1.5 pl-3 pr-3 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-indigo-600 sm:text-sm sm:leading-6">                
                <textarea
                  id="purpose"
                  {...register('purpose', { 
                    required: 'Please describe what you want to write about',
                    validate: value => {
                      const wordCount = (value || '').trim().split(/\s+/).filter(Boolean).length;
                      return wordCount >= 20 || 'Please provide a more detailed description (at least 20 words)';
                    },
                    onChange: (e) => {
                      const text = e.target.value || '';
                      const count = text.trim().split(/\s+/).filter(Boolean).length;
                      setWordCount(count);
                    }
                  })}
                  rows={5}
                  className="block w-full border-none focus:ring-0 focus:outline-none text-base py-1 px-1 resize-none"
                  placeholder="Describe the topic and main points you want to cover in your article..."
                  style={{ width: '100%' }}
                />
              </div>
              {errors.purpose && (
                <p className="mt-1 text-sm text-red-600">{errors.purpose.message}</p>
              )}
              <div className="flex justify-between items-center">
                <p className="mt-1 text-sm text-gray-500">
                  Be specific about your topic and what you want readers to learn or understand
                </p>
                <p className={`text-sm ${wordCount >= 20 ? 'text-green-600' : 'text-amber-600'}`}>
                  {wordCount} / 20 words {wordCount >= 20 ? '✓' : ''}
                </p>
              </div>
            </div>

            <div>
              <div className="flex items-center mb-2">
                <LightBulbIcon className="h-5 w-5 text-blue-500 mr-2" />
                <label htmlFor="intent" className="block text-sm font-medium text-gray-700">
                  What purpose does this article serve?
                </label>
              </div>
              <div className="rounded-md shadow-sm">
                <TaggedSelect
                  value={selectedIntents}
                  error={errors.intent?.message}
                  onChange={(value) => {
                    const intents = Array.isArray(value) ? value : [];
                    setSelectedIntents(intents);
                    setValue('intent', intents.join(','));
                  }}
                  options={intentOptions}
                  placeholder="Select your primary intent..."
                  multiple={true}
                  editable={false}
                  className="min-h-[44px] py-1"
                />
                {errors.intent && (
                  <p className="mt-1 text-sm text-red-600">{errors.intent.message}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  We recommend selecting at most 3 purposes
                </p>
              </div>
            </div>

            <div>
              <div className="flex items-center mb-2">
                <UserGroupIcon className="h-5 w-5 text-blue-500 mr-2" />
                <label htmlFor="targetAudience" className="block text-sm font-medium text-gray-700">
                  Who is your target audience?
                </label>
              </div>
              <div className="rounded-md shadow-sm">
                <TaggedSelect
                  value={selectedAudiences}
                  error={errors.targetAudience?.message}
                  onChange={(value) => {
                    const audiences = Array.isArray(value) ? value : [];
                    setSelectedAudiences(audiences);
                    setValue('targetAudience', audiences.join(','));
                  }}
                  options={audienceOptions}
                  placeholder="Select your target audience..."
                  multiple={true}
                  editable={false}
                  className="min-h-[44px] py-1"
                />
                {errors.targetAudience && (
                  <p className="mt-1 text-sm text-red-600">{errors.targetAudience.message}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  Select the primary audience for your article
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Style & Tags */}
        {currentStep === 'style' && (
          <div className="space-y-6">
            <p className="text-gray-600">
              Further customize your article, like the keywords you want to cover and the styles!
            </p>
            <div>
              <div className="flex items-center mb-2">
                <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
                  Select tags you want to cover in this article:
                </label>
              </div>
              <TaggedSelect
                value={selectedTags}
                onChange={handleTagsChange}
                options={businessKeywords.map(kw => ({ value: kw, label: kw }))}
                placeholder="Select or add tags..."
                multiple={true}
                editable={true}
                className="min-h-[44px] py-1"
                onCreateOption={handleCreateTag}
              />
              <p className="mt-1 text-sm text-gray-500">
                Select from your business keywords or add new ones as tags for this article
              </p>
            </div>

            <div>
              <div className="flex items-center mb-2">
                <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                  Article Category
                </label>
              </div>
              <TaggedSelect
                value={selectedCategory}
                error={errors.category?.message}
                onChange={(value) => {
                  const category = Array.isArray(value) ? value[0] || '' : value || '';
                  setSelectedCategory(category);
                  setValue('category', category, { shouldValidate: true });
                }}
                options={[...categories, ...businessCategories.map(cat => ({ value: cat, label: cat }))].filter((option, index, self) => 
                  index === self.findIndex(o => o.value === option.value)
                )}
                placeholder="Select or create a category..."
                multiple={false}
                editable={true}
                className="min-h-[44px] py-1"
                onCreateOption={(newCategory) => {
                  if (!businessCategories.includes(newCategory)) {
                    setBusinessCategories(prev => [...prev, newCategory]);
                  }
                  setSelectedCategory(newCategory);
                  setValue('category', newCategory, { shouldValidate: true });
                }}
              />
              {errors.category && (
                <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Choose an existing category or type to create a new one.
              </p>
            </div>

            <div>
              <div className="flex items-center mb-2">
                <SpeakerWaveIcon className="h-5 w-5 text-blue-500 mr-2" />
                <label htmlFor="tone" className="block text-sm font-medium text-gray-700">
                  Set the tone of voice for this article:
                </label>
              </div>
              <div className="rounded-md shadow-sm">
                <TaggedSelect
                  value={selectedTone}
                  error={errors.tone?.message}
                  onChange={(value) => {
                    if (Array.isArray(value)) {
                      if (value.length === 0) {
                        setSelectedTone('');
                        setValue('tone', '');
                      } else {
                        setSelectedTone(value[0]);
                        setValue('tone', value[0]);
                      }
                    } else if (typeof value === 'string') {
                      setSelectedTone(value);
                      setValue('tone', value);
                    }
                  }}
                  options={toneOptions}
                  placeholder="Select tone of voice..."
                  multiple={false}
                  editable={false}
                  className="min-h-[44px] py-1"
                />
              </div>
              {errors.tone && (
                <p className="mt-1 text-sm text-red-600">{errors.tone.message}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                You can customize the tone of voice for this article
              </p>
            </div>

            <div>
              <div className="flex items-center mb-2">
                <ClockIcon className="h-5 w-5 text-blue-500 mr-2" />
                <label htmlFor="desiredLength" className="block text-sm font-medium text-gray-700">
                  Desired article length
                </label>
              </div>
              <div className="rounded-md shadow-sm">
                <TaggedSelect
                  value={selectedLength}
                  error={errors.desiredLength?.message}
                  onChange={(value) => {
                    if (Array.isArray(value)) {
                      if (value.length === 0) {
                        setSelectedLength('' as 'short' | 'medium' | 'long');
                        setValue('desiredLength', '' as 'short' | 'medium' | 'long');
                      } else {
                        const length = value[0] as 'short' | 'medium' | 'long';
                        setSelectedLength(length);
                        setValue('desiredLength', length);
                      }
                    } else if (typeof value === 'string') {
                      const length = value as 'short' | 'medium' | 'long';
                      setSelectedLength(length);
                      setValue('desiredLength', length);
                    }
                  }}
                  options={lengthOptions}
                  placeholder="Select article length..."
                  multiple={false}
                  editable={false}
                  className="min-h-[44px] py-1"
                />
              </div>
              {errors.desiredLength && (
                <p className="mt-1 text-sm text-red-600">{errors.desiredLength.message}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Select the desired article length. You need to decide this according to your audiences!
              </p>
            </div>
          </div>
        )}

      </form>
    </div>
  );
});

export default ManualMode;
